import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { SectionHead, Reveal } from "@/lib/motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { subscribeContactPrefill } from "@/lib/contactBus";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(
    () =>
      subscribeContactPrefill((msg) => {
        setForm((f) => ({ ...f, message: msg }));
        toast.info("Formulaire prérempli : complétez vos coordonnées.");
      }),
    []
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Votre message a bien été envoyé. Nous vous répondons rapidement.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32" data-testid="section-contact">
      <div className="mx-auto max-w-6xl px-5">
        <SectionHead tag="Contact" title="Parlez-nous de votre besoin, nous vous répondons rapidement." />
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="h-full rounded-2xl bg-korban-navy2/50 p-8 ring-1 ring-white/10 md:p-10">
              <h3 className="font-display text-2xl font-semibold text-korban-gold">Nos coordonnées</h3>
              <ul className="mt-8 space-y-6 text-slate-200">
                <li className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-korban-gold/15">
                    <Phone size={17} className="text-korban-gold" />
                  </span>
                  <a href="tel:+2250769545469" data-testid="contact-phone-link" className="pt-2 font-semibold hover:text-korban-gold">
                    07 69 54 54 69
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-korban-gold/15">
                    <Mail size={17} className="text-korban-gold" />
                  </span>
                  <a href="mailto:Korbanholding@gmail.com" data-testid="contact-email-link" className="break-all pt-2 font-semibold hover:text-korban-gold">
                    Korbanholding@gmail.com
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-korban-gold/15">
                    <MapPin size={17} className="text-korban-gold" />
                  </span>
                  <span className="pt-2 font-semibold">Yopougon, China Mall, appartement 205</span>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="grid gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-8 md:p-10"
            >
              <div>
                <label htmlFor="c-name" className="mb-1.5 block text-sm font-semibold text-slate-200">Nom</label>
                <input id="c-name" required minLength={2} value={form.name} onChange={set("name")}
                  placeholder="Votre nom" data-testid="contact-input-name" className="field" />
              </div>
              <div>
                <label htmlFor="c-email" className="mb-1.5 block text-sm font-semibold text-slate-200">Email</label>
                <input id="c-email" type="email" required value={form.email} onChange={set("email")}
                  placeholder="vous@entreprise.ci" data-testid="contact-input-email" className="field" />
              </div>
              <div>
                <label htmlFor="c-msg" className="mb-1.5 block text-sm font-semibold text-slate-200">Votre besoin</label>
                <textarea id="c-msg" required minLength={5} rows={5} value={form.message} onChange={set("message")}
                  placeholder="Décrivez votre besoin : paie, RH, fiscalité, informatisation…"
                  data-testid="contact-input-message" className="field resize-none" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-korban-gold px-7 py-3.5 font-bold text-korban-navy transition-all hover:-translate-y-0.5 hover:bg-korban-golddark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                {sending ? "Envoi en cours…" : "Envoyer"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
